import React, { useState, useEffect } from 'react';
import { GlobalUser, ProjectInfo, CompanyConfig, Profile, CNCGroup, ConstraintGroup, SubcontractType, Incidence, AppNotification } from '../types';
import DocumentManager from '../components/admin/DocumentManager';
import CNCManager from '../components/admin/CNCManager';
import ConstraintManager from '../components/admin/ConstraintManager';
import SubcontractTypeManager from '../components/admin/SubcontractTypeManager';
import BackupsManager from '../components/admin/BackupsManager';
import { supabase } from '../supabaseClient';

interface GlobalAdminPanelProps {
    onBack: () => void;
    globalUsers: GlobalUser[];
    setGlobalUsers: React.Dispatch<React.SetStateAction<GlobalUser[]>>;
    globalProfiles: Profile[];
    setGlobalProfiles: React.Dispatch<React.SetStateAction<Profile[]>>;
    projects: (ProjectInfo & { id: string })[];
    setProjects: React.Dispatch<React.SetStateAction<(ProjectInfo & { id: string })[]>>;
    companyConfig: CompanyConfig;
    globalCNCGroups: CNCGroup[];
    setGlobalCNCGroups: React.Dispatch<React.SetStateAction<CNCGroup[]>>;
    globalConstraintGroups: ConstraintGroup[];
    setGlobalConstraintGroups: React.Dispatch<React.SetStateAction<ConstraintGroup[]>>;
    globalSubcontractTypes: SubcontractType[];
    setGlobalSubcontractTypes: React.Dispatch<React.SetStateAction<SubcontractType[]>>;
    incidences: Incidence[];
    setIncidences: React.Dispatch<React.SetStateAction<Incidence[]>>;
    notifications?: AppNotification[];
    setNotifications?: React.Dispatch<React.SetStateAction<AppNotification[]>>;
}

const GlobalAdminPanel: React.FC<GlobalAdminPanelProps> = ({ 
    onBack, globalUsers, setGlobalUsers, globalProfiles, setGlobalProfiles, projects, setProjects, companyConfig,
    globalCNCGroups, setGlobalCNCGroups, globalConstraintGroups, setGlobalConstraintGroups, globalSubcontractTypes, setGlobalSubcontractTypes,
    incidences, setIncidences, notifications, setNotifications
}) => {
    const [activeTab, setActiveTab] = useState<'usuarios' | 'obras' | 'perfiles' | 'empresas' | 'documentos' | 'maestros' | 'backups' | 'incidencias'>('usuarios');
    const [activeMaestro, setActiveMaestro] = useState<'list' | 'cnc' | 'restricciones' | 'subcontratos'>('list');
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    
    // Invite State
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [inviteProject, setInviteProject] = useState('');
    const [inviteRole, setInviteRole] = useState('');
    const [isInviting, setIsInviting] = useState(false);

    // Project State
    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
    const [newProject, setNewProject] = useState({ name: '', startDate: '', endDate: '', status: 'Active' as 'Active' | 'Inactive' });
    const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
    const [projectFilter, setProjectFilter] = useState<'Active' | 'Inactive'>('Active');

    // User Edit State
    const [isUserEditModalOpen, setIsUserEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<GlobalUser | null>(null);
    const [isDeletingUser, setIsDeletingUser] = useState<string | null>(null);

    const handleInviteUser = async () => {
        if (!inviteEmail || !inviteName || !inviteProject || !inviteRole) return;
        setIsInviting(true);

        try {
            const roleName = globalProfiles.find(p => p.id === inviteRole)?.name || inviteRole;
            // Invocar la Edge Function para despachar el correo a través de Supabase
            const { data, error } = await supabase.functions.invoke('invite-user', {
                body: { email: inviteEmail, name: inviteName, role: roleName, projectId: inviteProject }
            });

            if (error || (data && data.error)) {
                const actualError = error?.message || data?.error;
                console.error("Function Error:", actualError);
                
                // Interceptar error 422 (Usuario ya registrado / conflicto)
                if (actualError && (actualError.includes('422') || actualError.includes('already_registered') || actualError.includes('registered'))) {
                    alert(`El correo ${inviteEmail} ya se encuentra registrado o invitado previamente.`);
                } else {
                    alert("No pudimos enviar la invitación. Por favor intenta más tarde.\nDetalle del Servidor: " + actualError);
                }
                
                setIsInviting(false);
                return;
            }

            if (data && data.email_status === 'failed') {
                alert(`⚠️ El usuario fue creado exitosamente en el sistema, PERO el correo de invitación falló al enviarse.\n\nMotivo del servidor de correo: ${data.email_error}\n\nPor favor, contacta a TI para revisar la configuración SMTP o comparte el enlace de acceso manualmente:\n${data.action_link}`);
            }

            const exists = globalUsers.find(u => u.email.toLowerCase() === inviteEmail.toLowerCase());
            
            if (exists) {
                setGlobalUsers(globalUsers.map(u => {
                    if (u.email.toLowerCase() === inviteEmail.toLowerCase()) {
                        return {
                            ...u,
                            status: 'Active',
                            assignedProjects: [
                                ...u.assignedProjects.filter(ap => ap.projectId !== inviteProject),
                                { projectId: inviteProject, profileId: inviteRole, profileName: roleName }
                            ]
                        };
                    }
                    return u;
                }));
            } else {
                const newUser: GlobalUser = {
                    id: data?.user?.id || 'temp-' + Date.now().toString(),
                    email: inviteEmail.toLowerCase(),
                    name: inviteName,
                    globalRole: 'Usuario',
                    status: 'Pending',
                    assignedProjects: [{ projectId: inviteProject, profileId: inviteRole, profileName: roleName }]
                };
                setGlobalUsers([...globalUsers, newUser]);
            }
            
            setIsInviteModalOpen(false);
            setInviteEmail('');
            setInviteName('');
            setInviteProject('');
            setInviteRole('');
            alert(`¡Listo! Hemos enviado la invitación de acceso a ${inviteEmail}.`);
        } catch (err: any) {
            console.error("Network Error:", err);
            alert("Error de red al invitar al usuario. ¿Está en línea la función de edge de Supabase?");
        } finally {
            setIsInviting(false);
        }
    };

    const toggleGlobalRole = (email: string, currentRole: 'Administrador' | 'Usuario') => {
        setGlobalUsers(globalUsers.map(u => {
            if (u.email === email) {
                return { ...u, globalRole: currentRole === 'Administrador' ? 'Usuario' : 'Administrador' };
            }
            return u;
        }));
    };

    const handleSaveProject = () => {
        if (!newProject.name || !newProject.startDate || !newProject.endDate) return;
        
        if (editingProjectId) {
            setProjects(projects.map(p => 
                p.id === editingProjectId ? { ...newProject, id: editingProjectId } : p
            ));
        } else {
            const project = {
                ...newProject,
                id: Date.now().toString()
            };
            setProjects([...projects, project]);
        }
        
        setIsProjectModalOpen(false);
        setNewProject({ name: '', startDate: '', endDate: '', status: 'Active' });
        setEditingProjectId(null);
    };

    const handleEditProject = (p: ProjectInfo & { id: string }) => {
        setNewProject({ name: p.name, startDate: p.startDate, endDate: p.endDate, status: p.status || 'Active' });
        setEditingProjectId(p.id);
        setIsProjectModalOpen(true);
    };

    const handleSaveUser = () => {
        if (!editingUser) return;
        setGlobalUsers(globalUsers.map(u => 
            u.id === editingUser.id ? editingUser : u
        ));
        setIsUserEditModalOpen(false);
        setEditingUser(null);
    };

    const handleDeleteProject = (pId: string, pName: string) => {
        // Backend Validation Logic
        const usersWithProject = globalUsers.filter(u => 
            u.assignedProjects.some(ap => ap.projectId === pId)
        );

        if (usersWithProject.length > 0) {
            alert(`⛔ VALIDADOR DE ELIMINACIÓN:\n\nLa obra "${pName}" tiene actualmente ${usersWithProject.length} usuario(s) asignado(s) y contiene historial operativo o protocolos asociados.\n\nPor seguridad e integridad relacional, no puede eliminarse permanentemente. Para archivarla, por favor use el botón Editar y cambie su Estado a "Inactiva".`);
            return;
        }

        if(confirm(`¿Estás seguro de eliminar permanentemente la obra en blanco "${pName}"?`)) {
            setProjects(projects.filter(p => p.id !== pId));
        }
    };

    const handleDeleteUser = async (uId: string, uEmail: string, assignedCount: number) => {
        // Backend Validation Logic
        if (assignedCount > 0) {
            alert(`⛔ VALIDADOR DE ELIMINACIÓN:\n\nEl usuario ${uEmail} tiene vinculación activa con ${assignedCount} perfiles operativos en sus obras.\n\nPara evitar pérdidas de trazabilidad en sistemas de calidad o auditoría, no puede ser eliminado permanentemente de la empresa mientras tenga actividad viva.\nPor favor, Edite al usuario y retire sus asignaciones primero.`);
            return;
        }

        if(confirm(`El usuario ${uEmail} no tiene operaciones vinculadas. ¿Desea eliminarlo del directorio maestro de la empresa permanentemente?`)) {
            setIsDeletingUser(uId);
            try {
                // Sincronizar borrado absoluto en la matriz de seguridad Supabase (auth.users)
                const { data, error } = await supabase.functions.invoke('admin-delete-user', {
                    body: { email: uEmail }
                });

                if (error || (data && data.error)) {
                    console.error("Server Delete Error:", error || data.error);
                    alert("No se pudo purgar la cuenta de seguridad maestra en Supabase.\nDetalle técnico: " + (error?.message || data?.error));
                    setIsDeletingUser(null);
                    return;
                }

                // Si tiene éxito en la nube, borramos de la memoria visual y base distribuida
                setGlobalUsers(globalUsers.filter(u => u.id !== uId));
            } catch (err) {
                console.error("Network Error deleting user:", err);
                alert("Error crítico de red conectado al servidor de borrado.");
            } finally {
                setIsDeletingUser(null);
            }
        }
    };

    const toggleUserStatus = (uId: string, currentStatus: string) => {
        const newStatus = currentStatus === 'Inactive' ? 'Active' : 'Inactive';
        setGlobalUsers(globalUsers.map(u => 
            u.id === uId ? { ...u, status: newStatus as any } : u
        ));
    };

    const navItems = [
        { id: 'usuarios', label: 'Usuarios', icon: 'manage_accounts' },
        { id: 'obras', label: 'Obras', icon: 'apartment' },
        { id: 'perfiles', label: 'Perfiles', icon: 'shield' },
        { id: 'empresas', label: 'Empresas', icon: 'domain' },
        { id: 'maestros', label: 'Maestros', icon: 'settings_applications' },
        { id: 'documentos', label: 'Gestor Documental', icon: 'folder_open' },
        { id: 'backups', label: 'Copias de Seguridad', icon: 'history' },
        { id: 'incidencias', label: 'Registro Incidencias', icon: 'bug_report' },
    ] as const;

    return (
        <div className="flex flex-col md:flex-row h-screen w-full overflow-hidden bg-slate-50 dark:bg-[#0d131b] font-display">
            
            {/* Mobile Header (Hidden on Desktop) */}
            <header 
                className="md:hidden flex items-center justify-between p-4 bg-white dark:bg-[#1a2634] border-b border-slate-200 dark:border-slate-800 z-50 shadow-sm shrink-0"
                style={{
                    paddingTop: "calc(1rem + env(safe-area-inset-top, 0px))"
                }}
            >
                <div className="flex items-center gap-3">
                    <button
                        onClick={onBack}
                        className="flex items-center justify-center size-8 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-500 transition-all"
                    >
                        <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                    </button>
                    <div className="flex flex-col">
                        <h1 className="text-base font-black text-slate-900 dark:text-white leading-tight">Administración General</h1>
                        <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">Configuración</p>
                    </div>
                </div>
            </header>

            {/* Mobile Nav Tabs (Hidden on Desktop) */}
            <nav className="md:hidden flex w-full bg-white dark:bg-[#1a2634] border-b border-slate-200 dark:border-slate-800 shrink-0">
                <div className="flex justify-between gap-1 p-3 w-full overflow-x-auto">
                    {navItems.filter(item => item.id !== 'backups').map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`flex flex-1 items-center justify-center px-1 py-2 rounded-xl transition-colors whitespace-nowrap text-[11px] font-bold ${activeTab === item.id
                                ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800"
                                : "text-slate-500 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-100 dark:border-slate-700"
                                }`}
                        >
                            {item.id === 'documentos' ? 'GD' : item.label}
                        </button>
                    ))}
                </div>
            </nav>

            {/* Desktop Sidebar (Hidden on Mobile) */}
            <aside className="hidden md:flex h-full w-64 flex-col border-r border-[#e7ecf3] bg-white dark:bg-[#1a2634] dark:border-[#2a3644] flex-shrink-0 z-50 shadow-sm">
                <div className="flex h-full flex-col justify-between p-4">
                    <div className="flex flex-col gap-6">
                        <div className="flex items-center gap-3 px-2">
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center size-8 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-500 transition-all mr-1"
                                title="Volver al menú principal"
                            >
                                <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                            </button>
                            <div className="flex items-center justify-center rounded-xl size-10 flex-shrink-0 overflow-hidden bg-white border border-slate-100 dark:border-slate-800 p-1">
                                <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt="Logo" className="hidden md:block w-full h-full object-contain" />
                                <img src="/assets/play_store_icon.png" alt="K-LyDapp" className="block md:hidden w-full h-full object-contain rounded-md" />
                            </div>
                            <div className="flex flex-col flex-1 truncate">
                                <h1 className="text-base font-bold leading-normal text-slate-900 dark:text-white truncate">Administración General</h1>
                                <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest truncate">Configuración</p>
                            </div>
                        </div>
                        <nav className="flex flex-col gap-2">
                            {navItems.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveTab(item.id)}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${activeTab === item.id
                                        ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 font-bold"
                                        : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 font-medium"
                                        }`}
                                >
                                    <span className="material-symbols-outlined text-[24px]">{item.icon}</span>
                                    <span className="text-sm">{item.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <main 
                className="flex-1 overflow-y-auto relative w-full"
                style={{
                    paddingBottom: "env(safe-area-inset-bottom, 0px)"
                }}
            >
                
            {activeTab === 'usuarios' && (
                <div className="p-4 md:p-8 space-y-4">
                    <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 bg-white dark:bg-[#1a2634] p-4 md:p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                        <div>
                            <h2 className="text-base font-black text-slate-800 dark:text-white">Directorio de Usuarios</h2>
                            <p className="text-xs text-slate-500 mt-1">Personas registradas o invitadas a la plataforma.</p>
                        </div>
                        <button 
                            onClick={() => setIsInviteModalOpen(true)}
                            className="bg-primary hover:bg-blue-600 text-white px-4 py-2 text-sm rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-primary/20"
                        >
                            <span className="material-symbols-outlined text-[18px]">person_add</span>
                            Invitar a un Usuario
                        </button>
                    </div>

                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-[#1e2c3c] border-b border-slate-200 dark:border-slate-800">
                                    <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">Usuarios</th>
                                    <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest text-left md:text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {globalUsers.map(u => (
                                    <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-[#1e2a3b] transition-colors">
                                        <td className="p-4">
                                            <p className="font-bold text-[11px] text-slate-800 dark:text-white flex items-center gap-2">
                                                {u.name}
                                                {u.status === 'Inactive' && (
                                                    <span className="px-2 py-0.5 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 text-[9px] font-bold rounded-full select-none">INACTIVO</span>
                                                )}
                                            </p>
                                            <p className="text-[10px] text-slate-500 mb-1">{u.email}</p>
                                            <div className="flex flex-col gap-1 items-start mt-1.5">
                                                <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-md ${
                                                    u.globalRole === 'Administrador' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                                }`}>
                                                    {u.globalRole}
                                                </span>
                                                {u.defaultProjectId && (
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                                        <span className="material-symbols-outlined text-[10px] text-amber-500">star</span>
                                                        {projects.find(p => p.id === u.defaultProjectId)?.name || 'Obra Desconocida'}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        <td className="p-3 md:p-4 text-left md:text-right">
                                            <div className="flex flex-wrap justify-start md:justify-end gap-1.5">
                                                <button 
                                                    onClick={() => { setEditingUser({ ...u }); setIsUserEditModalOpen(true); }}
                                                    className="text-slate-500 hover:text-amber-600 text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors flex items-center gap-1"
                                                >
                                                    <span className="hidden md:inline-block material-symbols-outlined text-[16px]">manage_accounts</span>
                                                    Editar
                                                </button>
                                                <button 
                                                    onClick={() => toggleGlobalRole(u.email, u.globalRole)}
                                                    className="hidden md:inline-flex text-primary hover:text-blue-700 text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                                >
                                                    {u.globalRole === 'Administrador' ? 'Quitar Admin' : 'Hacer Admin'}
                                                </button>
                                                <button 
                                                    onClick={() => toggleUserStatus(u.id, u.status)}
                                                    className={`${u.status === 'Inactive' ? 'text-slate-400 hover:text-green-500 hover:bg-slate-100 dark:hover:bg-slate-800' : 'text-green-500 hover:text-slate-500 hover:bg-green-50 dark:hover:bg-green-900/20'} text-xs font-bold px-3 py-1.5 rounded-lg transition-colors`}
                                                    title={u.status === 'Inactive' ? "Usuario Inactivo - Clic para Reactivar" : "Usuario Activo - Clic para Inactivar"}
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">
                                                        {u.status === 'Inactive' ? 'person_off' : 'person_check'}
                                                    </span>
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteUser(u.id, u.email, u.globalRole === 'Administrador' ? 99 : u.assignedProjects.length)}
                                                    disabled={isDeletingUser === u.id}
                                                    className={`${isDeletingUser === u.id ? 'text-red-300 animate-pulse cursor-not-allowed' : 'text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'} text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center justify-center`}
                                                    title={isDeletingUser === u.id ? "Eliminando Servidor..." : "Eliminar Usuario"}
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">
                                                        {isDeletingUser === u.id ? 'hourglass_empty' : 'person_remove'}
                                                    </span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'obras' && (
                <div className="p-4 md:p-8 space-y-4">
                    <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 bg-white dark:bg-[#1a2634] p-4 md:p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                        <div>
                            <h2 className="text-lg font-black text-slate-800 dark:text-white">Registro de Obras</h2>
                            <p className="text-xs text-slate-500 mt-1">Crea y gestiona todas las obras vigentes e inactivas de la empresa.</p>
                        </div>
                        <button 
                            onClick={() => {
                                setNewProject({ name: '', startDate: '', endDate: '', status: 'Active' });
                                setEditingProjectId(null);
                                setIsProjectModalOpen(true);
                            }}
                            className="bg-primary hover:bg-blue-600 text-white px-4 py-2 text-sm rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-primary/20"
                        >
                            <span className="material-symbols-outlined text-[18px]">add_circle</span>
                            Nueva Obra
                        </button>
                    </div>

                    <div className="flex justify-center gap-4 mb-4">
                        <button 
                            onClick={() => setProjectFilter('Active')}
                            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${projectFilter === 'Active' ? 'bg-primary text-white shadow-md' : 'bg-white text-slate-500 dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800'}`}
                        >
                            Obras Activas
                        </button>
                        <button 
                            onClick={() => setProjectFilter('Inactive')}
                            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${projectFilter === 'Inactive' ? 'bg-slate-600 text-white shadow-md dark:bg-slate-700' : 'bg-white text-slate-500 dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800'}`}
                        >
                            Obras Inactivas
                        </button>
                    </div>

                    {/* Detailed List View for Projects */}
                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-[#1e2c3c] border-b border-slate-200 dark:border-slate-800">
                                    <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">Obras</th>
                                    <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">Estado</th>
                                    <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {projects.filter(p => (p.status || 'Active') === projectFilter).map(p => (
                                    <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-[#1e2a3b] transition-colors">
                                        <td className="p-4 text-[11px] font-bold text-slate-800 dark:text-white">{p.name}</td>
                                        <td className="p-4">
                                            <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full ${
                                                (!p.status || p.status === 'Active') ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                            }`}>
                                                {p.status || 'Active'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-1">
                                                <button 
                                                    onClick={() => handleEditProject(p)}
                                                    className="text-slate-400 hover:text-primary transition-colors p-2 rounded-lg hover:bg-primary/10"
                                                    title="Editar Obra"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">edit</span>
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteProject(p.id, p.name)}
                                                    className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                                                    title="Eliminar Obra"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {projects.filter(p => (p.status || 'Active') === projectFilter).length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="p-8 text-center text-slate-500 text-sm">
                                            No hay obras {projectFilter === 'Active' ? 'activas' : 'inactivas'} en este momento.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'perfiles' && (
                <div className="p-2 md:p-6">
                    <div className="bg-white dark:bg-[#1a2634] rounded-2xl p-4 md:p-8 border border-slate-200 dark:border-slate-800">
                        <ProfilesAndRoles profiles={globalProfiles} setProfiles={setGlobalProfiles} globalUsers={globalUsers} />
                    </div>
                </div>
            )}

            {activeTab === 'empresas' && (
                <div className="p-4 md:p-8">
                    <div className="flex flex-col items-center justify-center bg-white dark:bg-[#1a2634] p-8 md:p-12 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm text-center">
                        <div className="w-24 h-24 rounded-2xl bg-white border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex items-center justify-center mb-6 p-2">
                            <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt="Logo Empresa" className="hidden md:block w-full h-full object-contain" />
                            <img src="/assets/play_store_icon.png" alt="K-LyDapp" className="block md:hidden w-full h-full object-contain rounded-xl" />
                        </div>
                        <h2 className="text-xl font-black text-slate-800 dark:text-white mb-2">Empresas</h2>
                        <p className="text-slate-500 max-w-md text-sm md:text-base">Actualmente operando bajo la empresa principal: <strong className="text-slate-700 dark:text-slate-300">{companyConfig.name}</strong>. El módulo multitenant de empresas adicionales estará disponible en la próxima actualización.</p>
                    </div>
                </div>
            )}
            
            {activeTab === 'maestros' && activeMaestro === 'list' && (
                <div className="p-4 md:p-8 space-y-4">
                    <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 bg-white dark:bg-[#1a2634] p-4 md:p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm mb-4">
                        <div>
                            <h2 className="text-lg font-black text-slate-800 dark:text-white">Gestión de Maestros</h2>
                            <p className="text-xs text-slate-500 mt-1">Configuración centralizada de catálogos y clasificaciones del sistema.</p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <button onClick={() => setActiveMaestro('cnc')} className="flex items-center justify-between p-4 bg-white dark:bg-[#1a2634] hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl transition-all shadow-sm">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center justify-center size-12 rounded-xl bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                                    <span className="material-symbols-outlined">warning</span>
                                </div>
                                <div className="text-left">
                                    <h3 className="font-bold text-slate-800 dark:text-white text-sm md:text-base">Maestro de Causas de No Cumplimiento</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">Gestión de motivos de rechazo o incumplimiento</p>
                                </div>
                            </div>
                            <span className="material-symbols-outlined text-slate-400">chevron_right</span>
                        </button>

                        <button onClick={() => setActiveMaestro('restricciones')} className="flex items-center justify-between p-4 bg-white dark:bg-[#1a2634] hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl transition-all shadow-sm">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center justify-center size-12 rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                                    <span className="material-symbols-outlined">block</span>
                                </div>
                                <div className="text-left">
                                    <h3 className="font-bold text-slate-800 dark:text-white text-sm md:text-base">Maestro de Restricciones</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">Gestión de bloqueos y alertas operativas</p>
                                </div>
                            </div>
                            <span className="material-symbols-outlined text-slate-400">chevron_right</span>
                        </button>

                        <button onClick={() => setActiveMaestro('subcontratos')} className="flex items-center justify-between p-4 bg-white dark:bg-[#1a2634] hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl transition-all shadow-sm">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center justify-center size-12 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                                    <span className="material-symbols-outlined">handshake</span>
                                </div>
                                <div className="text-left">
                                    <h3 className="font-bold text-slate-800 dark:text-white text-sm md:text-base">Maestro de Tipos de Subcontratos</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">Clasificación de especialidades y contratos</p>
                                </div>
                            </div>
                            <span className="material-symbols-outlined text-slate-400">chevron_right</span>
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'maestros' && activeMaestro !== 'list' && (
                <div className="p-4 md:p-8 space-y-4">
                    <button 
                        onClick={() => setActiveMaestro('list')}
                        className="flex items-center gap-2 text-slate-500 hover:text-primary transition-colors text-sm font-bold mb-2 bg-white dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800 px-4 py-2 rounded-xl shadow-sm w-fit"
                    >
                        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                        Volver a Maestros
                    </button>
                    
                    <div className="bg-white dark:bg-[#1a2634] p-4 md:p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">

                        {activeMaestro === 'cnc' && <CNCManager groups={globalCNCGroups} setGroups={setGlobalCNCGroups} />}
                        {activeMaestro === 'restricciones' && <ConstraintManager groups={globalConstraintGroups} setGroups={setGlobalConstraintGroups} />}
                        {activeMaestro === 'subcontratos' && <SubcontractTypeManager types={globalSubcontractTypes} setTypes={setGlobalSubcontractTypes} />}
                    </div>
                </div>
            )}

            {activeTab === 'documentos' && (
                <DocumentManager projects={projects} />
            )}

            {activeTab === 'backups' && (
                <BackupsManager projects={projects} />
            )}

            {activeTab === 'incidencias' && (
                <IncidencePanel incidences={incidences} setIncidences={setIncidences} notifications={notifications} setNotifications={setNotifications} globalUsers={globalUsers} />
            )}

            {/* Invite Modal */}
            {isInviteModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700 animate-scale-up">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-[#1e2c3c]">
                            <div>
                                <h3 className="text-lg font-black text-slate-800 dark:text-white tracking-tight uppercase">Invitar a un usuario</h3>
                            </div>
                            <button onClick={() => setIsInviteModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-xl">
                                <div className="flex gap-3">
                                    <span className="material-symbols-outlined text-blue-500">mark_email_read</span>
                                    <div className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                                        <strong>Mensaje Automático a enviar:</strong><br/>
                                        "Hola,<br/>
                                        Has sido invitado a registrarte y colaborar en la plataforma de control de gestión K-LyDapp.<br/>
                                        Para darte de alta solo debes hacer clic en el botón a continuación e ingresar con tu credencial de Microsoft Office365."
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Nombre Completo</label>
                                <input
                                    type="text"
                                    value={inviteName}
                                    onChange={e => setInviteName(e.target.value)}
                                    placeholder="Ej: Juan Pérez"
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Correo (Microsoft 365)</label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={e => setInviteEmail(e.target.value)}
                                    placeholder="ejemplo@ldconstructora.cl"
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Obra a asignar</label>
                                <select 
                                    value={inviteProject}
                                    onChange={e => setInviteProject(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary appearance-none"
                                >
                                    <option value="">Selecciona una obra...</option>
                                    {projects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Perfil</label>
                                <select 
                                    value={inviteRole}
                                    onChange={e => setInviteRole(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary appearance-none"
                                >
                                    <option value="">Selecciona un perfil...</option>
                                    {globalProfiles.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                    {globalProfiles.length === 0 && (
                                        <option value="" disabled>Debes crear perfiles primero</option>
                                    )}
                                </select>
                            </div>

                            <button
                                onClick={handleInviteUser}
                                disabled={!inviteEmail || !inviteName || !inviteProject || !inviteRole || isInviting}
                                className="w-full py-4 bg-primary text-white rounded-xl font-bold hover:bg-blue-600 transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
                            >
                                <span className="material-symbols-outlined">{isInviting ? 'hourglass_empty' : 'send'}</span>
                                {isInviting ? 'Enviando...' : 'Enviar Invitación'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {isUserEditModalOpen && editingUser && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-xl w-full max-w-lg flex flex-col overflow-hidden animate-scale-up border border-slate-200 dark:border-slate-700">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1e2c3c] flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white uppercase tracking-tight">Editar Usuario</h3>
                                <p className="text-sm text-slate-500">{editingUser.email}</p>
                            </div>
                            <button onClick={() => setIsUserEditModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Nombre Completo</label>
                                <input
                                    type="text"
                                    value={editingUser.name}
                                    onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                                    className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold text-sm rounded-lg outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>
                            {editingUser.globalRole === 'Administrador' ? (
                                <div className="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 p-4 rounded-xl text-sm font-medium">
                                    <span className="material-symbols-outlined inline-block align-middle mr-2">info</span>
                                    Este usuario es <strong>Administrador General</strong>. Tiene acceso automático a todas las obras y configuración. No es necesario asignarle proyectos individualmente.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest">Adicionar Obra</label>
                                        <select 
                                            className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs rounded-lg outline-none cursor-pointer focus:ring-2 focus:ring-primary/20 appearance-none min-w-[200px]"
                                            value=""
                                            onChange={(e) => {
                                                const pId = e.target.value;
                                                const currentGrouped = editingUser.assignedProjects.reduce((acc, curr) => {
                                                    if (!acc[curr.projectId]) acc[curr.projectId] = [];
                                                    acc[curr.projectId].push(curr.profileId);
                                                    return acc;
                                                }, {} as Record<string, string[]>);
                                                
                                                if (pId && !currentGrouped[pId]) {
                                                    setEditingUser({ 
                                                        ...editingUser, 
                                                        assignedProjects: [...editingUser.assignedProjects, { projectId: pId, profileId: '' }] 
                                                    });
                                                }
                                            }}
                                        >
                                            <option value="">Selecciona para añadir...</option>
                                            {projects.filter(p => !editingUser.assignedProjects.find(ap => ap.projectId === p.id)).map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {editingUser.assignedProjects.length > 0 && (
                                        <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/10 p-3 rounded-xl border border-blue-200 dark:border-blue-800 mt-2">
                                            <label className="text-xs font-bold text-blue-700 dark:text-blue-400 uppercase tracking-widest flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">star</span> Obra Predeterminada</label>
                                            <select 
                                                className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs rounded-lg outline-none cursor-pointer focus:ring-2 focus:ring-primary/20 min-w-[200px] max-w-[200px] truncate"
                                                value={editingUser.defaultProjectId || ""}
                                                onChange={(e) => setEditingUser({ ...editingUser, defaultProjectId: e.target.value })}
                                            >
                                                <option value="">Ninguna</option>
                                                {projects.filter(p => editingUser.assignedProjects.find(ap => ap.projectId === p.id)).map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    
                                    {editingUser.assignedProjects.length === 0 ? (
                                        <div className="text-center p-6 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl text-slate-500 text-sm">
                                            Sin obras asignadas
                                        </div>
                                    ) : (
                                        <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-3 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent">
                                            {Object.entries(
                                                editingUser.assignedProjects.reduce((acc, curr) => {
                                                    if (!acc[curr.projectId]) acc[curr.projectId] = [];
                                                    acc[curr.projectId].push(curr.profileId);
                                                    return acc;
                                                }, {} as Record<string, string[]>)
                                            ).map(([projId, profiles]: [string, string[]]) => {
                                                const p = projects.find(proj => proj.id === projId);
                                                return (
                                                    <div key={projId} className="bg-white dark:bg-[#1e2c3c] rounded-xl p-5 border border-slate-200 dark:border-slate-700 relative shadow-sm">
                                                        <button 
                                                            onClick={() => setEditingUser({ ...editingUser, assignedProjects: editingUser.assignedProjects.filter(ap => ap.projectId !== projId) })}
                                                            className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition-colors bg-slate-50 dark:bg-slate-800 p-1.5 rounded-lg"
                                                            title="Remover Obra"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px]">delete</span>
                                                        </button>
                                                        <div className="font-bold text-sm text-slate-800 dark:text-white flex items-center gap-2 mb-4 pr-10 border-b border-slate-100 dark:border-slate-800 pb-3">
                                                            <span className="material-symbols-outlined text-green-500 text-[20px]">apartment</span>
                                                            {p?.name || 'Obra Desconocida'}
                                                        </div>
                                                        <div className="flex flex-col gap-3">
                                                            <div className="flex flex-wrap gap-2">
                                                                {profiles.filter(Boolean).map(prof => (
                                                                    <div key={prof} className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-2 border border-slate-200 dark:border-slate-700">
                                                                        <span className="material-symbols-outlined text-[14px] text-amber-500">shield</span>
                                                                        {globalProfiles.find(p => p.id === prof || p.name === prof)?.name || prof}
                                                                        <button 
                                                                            onClick={() => {
                                                                                const newAps = editingUser.assignedProjects.filter(ap => !(ap.projectId === projId && ap.profileId === prof));
                                                                                setEditingUser({ ...editingUser, assignedProjects: newAps });
                                                                            }}
                                                                            className="ml-1 text-slate-400 hover:text-red-500 transition-colors flex items-center justify-center p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[14px]">close</span>
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                                {profiles.filter(Boolean).length === 0 && (
                                                                    <span className="text-xs text-slate-400 italic py-1.5">Sin perfiles asignados en esta obra</span>
                                                                )}
                                                            </div>
                                                            <select 
                                                                className="px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-xs rounded-lg outline-none cursor-pointer focus:ring-2 focus:ring-primary/20 appearance-none w-fit"
                                                                value=""
                                                                onChange={(e) => {
                                                                    const selectedVal = e.target.value;
                                                                    if (selectedVal) {
                                                                        const profObj = globalProfiles.find(p => p.id === selectedVal || p.name === selectedVal);
                                                                        if (profObj && !profiles.includes(profObj.id) && !profiles.includes(profObj.name)) {
                                                                            // Clean up empty profile IDs if any exist for this project
                                                                            const cleaned = editingUser.assignedProjects.filter(ap => !(ap.projectId === projId && !ap.profileId));
                                                                            setEditingUser({ 
                                                                                ...editingUser, 
                                                                                assignedProjects: [...cleaned, { projectId: projId, profileId: profObj.id, profileName: profObj.name }] 
                                                                            });
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">+ Añadir perfil a la obra...</option>
                                                                {globalProfiles.filter(p => !profiles.includes(p.id) && !profiles.includes(p.name)).map(p => (
                                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    onClick={() => setIsUserEditModalOpen(false)}
                                    className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveUser}
                                    className="px-6 py-2 bg-primary text-white rounded-lg font-bold hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/20"
                                >
                                    Guardar Cambios
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Project Modal */}
            {isProjectModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden animate-scale-up border border-slate-200 dark:border-slate-700">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1e2c3c]">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">{editingProjectId ? 'Editar Obra' : 'Nueva Obra'}</h3>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Nombre Obra</label>
                                    <input
                                        type="text"
                                        value={newProject.name}
                                        onChange={e => setNewProject({ ...newProject, name: e.target.value })}
                                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Inicio</label>
                                    <input
                                        type="date"
                                        value={newProject.startDate}
                                        onChange={e => setNewProject({ ...newProject, startDate: e.target.value })}
                                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Fin Estimado</label>
                                    <input
                                        type="date"
                                        value={newProject.endDate}
                                        onChange={e => setNewProject({ ...newProject, endDate: e.target.value })}
                                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Estado Operativo</label>
                                    <select
                                        value={newProject.status}
                                        onChange={e => setNewProject({ ...newProject, status: e.target.value as 'Active' | 'Inactive' })}
                                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none"
                                    >
                                        <option value="Active">Activa</option>
                                        <option value="Inactive">Inactiva</option>
                                    </select>
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        onClick={() => setIsProjectModalOpen(false)}
                                        className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleSaveProject}
                                        disabled={!newProject.name || !newProject.startDate || !newProject.endDate}
                                        className="px-6 py-2 bg-primary text-white rounded-lg font-bold hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
                                    >
                                        {editingProjectId ? 'Guardar Cambios' : 'Crear Obra'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            </main>
        </div>
    );
};

// --- Sub-component for Accordion Rendering ---
const UserAssignedProjectsAccordion: React.FC<{ 
    assignedProjects: { projectId: string; profileId: string }[];
    projects: (ProjectInfo & { id: string })[] 
}> = ({ assignedProjects, projects }) => {
    const [isMainExpanded, setIsMainExpanded] = useState(false);
    const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
    
    // Group them
    const grouped = assignedProjects.reduce((acc, curr) => {
        if (!acc[curr.projectId]) acc[curr.projectId] = [];
        acc[curr.projectId].push(curr.profileId);
        return acc;
    }, {} as Record<string, string[]>);
    
    const projectIds = Object.keys(grouped).sort((a, b) => {
        const pa = projects.find(proj => proj.id === a)?.name || '';
        const pb = projects.find(proj => proj.id === b)?.name || '';
        return pa.localeCompare(pb);
    });
    
    if (projectIds.length === 0) return null;
    
    const firstProjectId = projectIds[0];
    const p1 = projects.find(proj => proj.id === firstProjectId);
    
    const toggleProject = (pId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedProjects(prev => ({ ...prev, [pId]: !prev[pId] }));
    };
    
    return (
        <div className="flex flex-col gap-1 w-full max-w-[300px]">
            <div 
                className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/50 p-1.5 -ml-1.5 rounded-lg transition-colors select-none group"
                onClick={() => setIsMainExpanded(!isMainExpanded)}
            >
                <div className={`w-5 h-5 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 transition-transform ${isMainExpanded ? 'rotate-90 bg-primary/10 text-primary' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200'}`}>
                    <span className="material-symbols-outlined text-[16px]">
                        chevron_right
                    </span>
                </div>
                <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold text-slate-800 dark:text-white flex items-center gap-1.5 truncate">
                        <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.5)]"></span>
                        <span className="truncate">{p1?.name || 'Obra Desconocida'}</span>
                    </span>
                    {projectIds.length > 1 && <span className="text-[10px] text-primary font-bold mt-0.5 opacity-80 group-hover:opacity-100 transition-opacity">y {projectIds.length - 1} obras más...</span>}
                </div>
            </div>
            
            {isMainExpanded && (
                <div className="pl-6 flex flex-col gap-3 mt-2 animate-fade-in border-l border-slate-200 dark:border-slate-800 ml-1">
                    {projectIds.map((projId) => {
                        const profiles = grouped[projId];
                        const p = projects.find(proj => proj.id === projId);
                        const isProjExpanded = expandedProjects[projId];
                        
                        return (
                            <div key={projId} className="flex flex-col relative">
                                <span className="absolute -left-6 top-2.5 w-4 h-px bg-slate-200 dark:bg-slate-800"></span>
                                <div 
                                    className="flex items-center justify-between cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/50 p-1.5 rounded-lg transition-colors select-none group/item"
                                    onClick={(e) => toggleProject(projId, e)}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={`material-symbols-outlined text-[14px] transition-transform ${isProjExpanded ? 'rotate-90 text-primary' : 'text-slate-400 group-hover/item:text-slate-600'}`}>
                                            arrow_right
                                        </span>
                                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                            {p?.name || 'Obra Desconocida'}
                                        </span>
                                    </div>
                                    <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                                        {profiles.length} perfil{profiles.length !== 1 ? 'es' : ''}
                                    </span>
                                </div>
                                
                                {isProjExpanded && (
                                    <div className="pl-6 mt-1.5 flex flex-col gap-1 border-l-2 border-slate-100 dark:border-slate-800 ml-2">
                                        {profiles.map((prof, idx) => (
                                            <div key={`${projId}-${idx}`} className="text-[10px] text-slate-600 dark:text-slate-400 font-bold flex items-center gap-1.5 bg-slate-50 dark:bg-[#1a2634] border border-slate-100 dark:border-slate-800 w-fit px-2.5 py-1 rounded-md shadow-sm relative">
                                                <span className="absolute -left-6 top-1/2 w-4 h-px bg-slate-100 dark:bg-slate-800"></span>
                                                <span className="material-symbols-outlined text-[12px] text-amber-500">shield</span>
                                                {prof || 'Sin Perfil'}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// --- Sub-component for Profiles and Roles (Migrated from Settings) ---
const ProfilesAndRoles: React.FC<{
    profiles: Profile[];
    setProfiles: React.Dispatch<React.SetStateAction<Profile[]>>;
    globalUsers?: GlobalUser[]; // Pass globalUsers for validation
}> = ({ profiles, setProfiles, globalUsers = [] }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProfile, setEditingProfile] = useState<Profile | null>(null);

    const initialPermissions = () => ({ view: false, edit: false });

    const modules = [
        {
            name: 'Incidencias',
            functionalities: [
                { id: 'mod_incidencias', name: 'Registro de Incidencias', onlyView: true }
            ]
        },
        {
            name: 'EDT',
            functionalities: [
                { id: 'mod_edt', name: 'Módulo Activo', onlyView: true },
                { id: 'edt_unidades', name: 'Unidad Física' },
                { id: 'edt_niveles', name: 'Niveles' },
                { id: 'edt_editar', name: 'Editar EDT' },
                { id: 'conf_avance_grafico', name: 'Configuración Avance Gráfico' }
            ]
        },
        {
            name: 'Planificación',
            functionalities: [
                { id: 'mod_planificacion', name: 'Módulo Activo', onlyView: true },
            ]
        },
        {
            name: 'Planificación Maestra',
            functionalities: [
                { id: 'mod_plan_maestra', name: 'Módulo Activo', onlyView: true },
                { id: 'pm_gantt_edit', name: 'Editar Carta Gantt' },
                { id: 'pm_gantt_imp', name: 'Importar Carta Gantt' },
                { id: 'pm_gantt_exp', name: 'Exportar Carta Gantt' },
                { id: 'pm_reprog', name: 'Cargar Reprogramación' },
                { id: 'pm_historial', name: 'Historial' },
                { id: 'pm_sync', name: 'Sincronizar Avance Semanal' }
            ]
        },
        {
            name: 'Planificación Intermedia',
            functionalities: [
                { id: 'mod_plan_intermedia', name: 'Módulo Activo', onlyView: true },
                { id: 'pi_historial', name: 'Historial', onlyView: true },
                { id: 'pi_ite', name: 'I.T.E', onlyView: true },
                { id: 'pi_generar', name: 'Generar Planificación' },
                { id: 'pi_planificaciones', name: 'Planificaciones' },
                { id: 'pi_restricciones', name: 'Restricciones' },
                { id: 'pi_eliminar', name: 'Eliminar Programación' }
            ]
        },
        {
            name: 'Planificación Semanal',
            functionalities: [
                { id: 'mod_plan_semanal', name: 'Módulo Activo', onlyView: true },
                { id: 'ps_historial', name: 'Historial', onlyView: true },
                { id: 'ps_crear', name: 'Crear Planificación' },
                { id: 'ps_desglose', name: 'Desglose' },
                { id: 'ps_planificaciones', name: 'Planificaciones' },
                { id: 'ps_gantt_avance', name: 'Avance por Carta Gantt' },
                { id: 'ps_eliminar', name: 'Eliminar Programación' }
            ]
        },
        {
            name: 'Control de Calidad',
            functionalities: [
                { id: 'mod_calidad', name: 'Módulo Activo', onlyView: true },
                { id: 'cc_config', name: 'Configuración de Protocolos' },
                { id: 'cc_completar', name: 'Completar Protocolos' }
            ]
        },
        {
            name: 'Subcontratos',
            functionalities: [
                { id: 'mod_subcontratos', name: 'Módulo Activo', onlyView: true },
                { id: 'sc_crear', name: 'Crear Empresa Contratista' },
                { id: 'sc_lista', name: 'Lista de Empresas Contratistas' },
                { id: 'sc_anadir', name: 'Añadir Contratistas' },
                { id: 'sc_editar', name: 'Editar' },
                { id: 'sc_inactivar', name: 'Inactivar' },
                { id: 'sc_anular', name: 'Anular' },
                { id: 'sc_activar', name: 'Activar' },
                { id: 'sc_eliminar', name: 'Eliminar' },
                { id: 'sc_generar_ep', name: 'Generar EP' },
                { id: 'sc_anexo', name: 'Anexo' },
                { id: 'sc_ver_ep', name: 'Ver EP' }
            ]
        },
        {
            name: 'Interfaz Móvil',
            functionalities: [
                { id: 'mod_movil', name: 'Módulo Activo', onlyView: true }
            ]
        },
        {
            name: 'Panel (Móvil)',
            functionalities: [
                { id: 'mod_panel', name: 'Módulo Activo', onlyView: true },
                { id: 'panel_semanal', name: 'Planificación Semanal', onlyView: true },
                { id: 'panel_intermedia', name: 'Planificación Intermedia', onlyView: true },
                { id: 'panel_ep', name: 'Estados de Pago', onlyView: true },
                { id: 'panel_protocolos', name: 'Protocolos', onlyView: true }
            ]
        },
        {
            name: 'Intermedia (Móvil)',
            functionalities: [
                { id: 'mod_movil_intermedia', name: 'Módulo Activo', onlyView: true },
                { id: 'movil_pi_restricciones', name: 'Completar Restricciones', onlyEdit: true }
            ]
        },
        {
            name: 'Semanal (Móvil)',
            functionalities: [
                { id: 'mod_movil_semanal', name: 'Módulo Activo', onlyView: true },
                { id: 'movil_ps_tareas', name: 'Completar Tareas', onlyEdit: true }
            ]
        },
        {
            name: 'Calidad (Móvil)',
            functionalities: [
                { id: 'mod_movil_calidad', name: 'Módulo Activo', onlyView: true },
                { id: 'movil_cc_todos', name: 'Visualizar todos los Protocolos', onlyView: true },
                { id: 'movil_cc_asignados', name: 'Visualizar protocolos asignados', onlyView: true },
                { id: 'movil_cc_completar', name: 'Completar Protocolos', onlyEdit: true }
            ]
        },
        {
            name: 'SC (Móvil)',
            functionalities: [
                { id: 'mod_movil_sc', name: 'Módulo Activo', onlyView: true },
                { id: 'movil_sc_ver_ep', name: 'Ver EP' },
                { id: 'movil_sc_generar_ep', name: 'Generar EP', onlyEdit: true }
            ]
        }
    ];

    const handleOpenModal = (profile?: Profile) => {
        if (profile) {
            const safePermissions = { ...profile.permissions };
            modules.forEach(mod => {
                mod.functionalities.forEach(func => {
                    if (!safePermissions[func.id]) {
                        safePermissions[func.id] = initialPermissions();
                    }
                });
            });
            setEditingProfile({
                ...profile,
                permissions: safePermissions
            });
        } else {
            setEditingProfile({
                id: Date.now().toString(),
                name: '',
                permissions: modules.reduce((acc, mod) => {
                    mod.functionalities.forEach(func => { acc[func.id] = initialPermissions(); }); return acc;
                }, {} as any)
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = () => {
        if (!editingProfile?.name) return;
        setProfiles(prev => {
            const exists = prev.find(p => p.id === editingProfile.id);
            if (exists) return prev.map(p => p.id === editingProfile.id ? editingProfile : p);
            return [...prev, editingProfile];
        });
        setIsModalOpen(false);
        setEditingProfile(null);
    };

    const togglePermission = (funcId: string, depth: 'view' | 'edit') => {
        if (!editingProfile) return;
        setEditingProfile({
            ...editingProfile,
            permissions: {
                ...editingProfile.permissions,
                [funcId]: {
                    ...editingProfile.permissions[funcId],
                    [depth]: !editingProfile.permissions[funcId]?.[depth]
                }
            }
        });
    };

    const handleDelete = (id: string, name: string, e: React.MouseEvent) => {
        e.stopPropagation();
        
        // Backend Validation Logic
        const usersWithProfile = globalUsers.filter(u => 
            u.assignedProjects.some(ap => ap.profileId === id || ap.profileId === name)
        );

        if (usersWithProfile.length > 0) {
            alert(`⛔ VALIDADOR DE ELIMINACIÓN:\n\nEl perfil "${name}" está actualmente asignado a ${usersWithProfile.length} usuario(s) en distintas obras.\n\nPor seguridad e integridad relacional, no puede ser eliminado hasta que sea removido de esos usuarios.`);
            return;
        }

        if(confirm(`¿Estás seguro de eliminar el perfil vacío "${name}" permanentemente?`)) {
            setProfiles(prev => prev.filter(p => p.id !== id));
        }
    };

    return (
        <div className="flex flex-col gap-3 md:gap-4">
            <div className="flex flex-col items-start gap-2 bg-white dark:bg-[#1a2634] pb-3 md:pb-4 border-b border-slate-200 dark:border-slate-800">
                <div>
                    <h2 className="text-base font-black text-slate-800 dark:text-white">Perfiles y Privilegios</h2>
                    <p className="text-[10px] md:text-xs text-slate-500 mt-1">Crea conjuntos de permisos que puedes asignar a los usuarios en las distintas obras.</p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 transition-all shadow-md shadow-amber-500/20"
                >
                    Nuevo Perfil
                </button>
            </div>
            <div className="bg-white dark:bg-[#1a2634] rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mt-1">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50 dark:bg-[#1e2c3c] border-b border-slate-200 dark:border-slate-800">
                            <th className="px-2 py-2 md:p-3 text-[9px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest w-full">Perfil</th>
                            <th className="px-1 py-2 md:p-3 text-[9px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest text-center shrink-0">Módulos</th>
                            <th className="px-2 py-2 md:p-3 text-[9px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest text-right shrink-0">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {profiles.map(p => (
                            <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-[#1e2a3b] transition-colors group cursor-pointer" onClick={() => handleOpenModal(p)}>
                                <td className="px-2 py-2.5 md:p-3 max-w-[150px] sm:max-w-[200px] md:max-w-none w-full">
                                    <h3 className="text-[11px] font-bold text-slate-800 dark:text-white whitespace-nowrap truncate">{p.name}</h3>
                                </td>
                                <td className="px-1 py-2.5 md:p-3 text-center shrink-0">
                                    <span className="px-1.5 md:px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[8px] md:text-[9px] font-black uppercase tracking-widest rounded-full whitespace-nowrap">
                                        {Object.keys(p.permissions).length} Módulos
                                    </span>
                                </td>
                                <td className="px-2 py-2.5 md:p-3 text-right flex-shrink-0">
                                    <div className="flex justify-end gap-1">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleOpenModal(p); }}
                                            className="text-slate-400 hover:text-amber-500 transition-colors p-1.5 rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                            title="Editar Perfil"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">edit</span>
                                        </button>
                                        <button 
                                            onClick={(e) => handleDelete(p.id, p.name, e)} 
                                            className="text-slate-400 hover:text-red-500 transition-colors p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100"
                                            title="Eliminar Perfil"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">delete</span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {profiles.length === 0 && (
                            <tr>
                                <td colSpan={4} className="p-8 text-center text-slate-500 text-sm">
                                    No hay perfiles configurados. Crea uno nuevo para comenzar.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Permissions Modal */}
            {isModalOpen && editingProfile && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-up border border-slate-200 dark:border-slate-700">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-[#1e2c3c]">
                            <div>
                                <h3 className="text-lg font-black text-slate-800 dark:text-white tracking-tight">
                                    {editingProfile.id.startsWith('temp') ? 'Crear Perfil' : 'Editar Perfil'}
                                </h3>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6 flex-1 overflow-y-auto space-y-8">
                            <div className="flex flex-col gap-2 max-w-md">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Nombre del Perfil</label>
                                <input
                                    type="text"
                                    value={editingProfile.name}
                                    onChange={e => setEditingProfile({ ...editingProfile, name: e.target.value })}
                                    className="px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[11px] font-bold focus:ring-2 focus:ring-amber-500 outline-none transition-all"
                                    placeholder="Ej: Administrador, Jefe de Obra..."
                                />
                            </div>
                            <div className="space-y-6">
                                {modules.map(mod => (
                                    <div key={mod.name} className="space-y-2">
                                        <div className="bg-slate-100 dark:bg-slate-800/50 px-3 md:px-4 py-2 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-400 flex items-center justify-between">
                                            <div className="flex items-center gap-2 flex-1 truncate">
                                                <span className="material-symbols-outlined text-[16px]">settings</span>
                                                <span className="truncate">{mod.name}</span>
                                            </div>
                                            <div className="flex gap-2 shrink-0 ml-2">
                                                <span className="w-10 md:w-16 text-center text-[10px] md:text-[9px] font-black uppercase tracking-tighter text-slate-500">
                                                    <span className="md:hidden">V</span><span className="hidden md:inline">Ver</span>
                                                </span>
                                                <span className="w-10 md:w-16 text-center text-[10px] md:text-[9px] font-black uppercase tracking-tighter text-slate-500">
                                                    <span className="md:hidden">M</span><span className="hidden md:inline">Modificar</span>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            {mod.functionalities.map(func => (
                                                <div key={func.id} className="flex items-center justify-between px-3 py-1.5 bg-white dark:bg-slate-800/20 hover:bg-slate-50 dark:hover:bg-slate-700/30 rounded-lg transition-colors group">
                                                    <span className="text-[10px] leading-tight font-bold text-slate-700 dark:text-slate-300 flex-1 pr-4">{func.name}</span>
                                                    <div className="flex gap-2 shrink-0">
                                                        {(['view', 'edit'] as const).map(depth => (
                                                            <div key={depth} className="w-10 md:w-16 flex justify-center items-center">
                                                                {((depth === 'view' && !func.onlyEdit) || (depth === 'edit' && !func.onlyView)) ? (
                                                                    <button
                                                                        onClick={() => togglePermission(func.id, depth)}
                                                                        className={`w-7 h-5 flex items-center justify-center rounded border transition-all ${editingProfile.permissions[func.id]?.[depth]
                                                                            ? 'bg-amber-100 border-amber-500 text-amber-600 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-400'
                                                                            : 'border-slate-200 dark:border-slate-700 text-slate-300 dark:text-slate-600 hover:border-slate-400'
                                                                            }`}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[13px]">
                                                                            {editingProfile.permissions[func.id]?.[depth] ? 'check_box' : 'check_box_outline_blank'}
                                                                        </span>
                                                                    </button>
                                                                ) : (
                                                                    <div className="w-7 h-5"></div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 bg-slate-50 dark:bg-[#1e2c3c]">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 dark:hover:text-white transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={!editingProfile.name}
                                    className="px-8 py-2 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-all shadow-lg shadow-amber-500/20 disabled:opacity-50"
                                >
                                    Guardar Cambios
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Incidence panel for administrators ---
interface IncidencePanelProps {
    incidences: Incidence[];
    setIncidences: React.Dispatch<React.SetStateAction<Incidence[]>>;
    notifications?: AppNotification[];
    setNotifications?: React.Dispatch<React.SetStateAction<AppNotification[]>>;
    globalUsers: GlobalUser[];
}

const IncidencePanel: React.FC<IncidencePanelProps> = ({ 
    incidences, setIncidences, notifications, setNotifications, globalUsers 
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'Todos' | 'Pendiente' | 'Resuelto' | 'Descartado'>('Todos');
    const [priorityFilter, setPriorityFilter] = useState<'Todos' | 'Baja' | 'Media' | 'Alta'>('Todos');
    const [selectedIncidence, setSelectedIncidence] = useState<Incidence | null>(null);
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    
    const [editStatus, setEditStatus] = useState<'Pendiente' | 'Resuelto' | 'Descartado'>('Pendiente');
    const [observations, setObservations] = useState('');

    useEffect(() => {
        if (selectedIncidence) {
            setEditStatus(selectedIncidence.status);
            setObservations(selectedIncidence.observations || '');
        }
    }, [selectedIncidence]);

    const handleSaveDetail = () => {
        if (!selectedIncidence) return;
        
        setIncidences(prev => prev.map(inc => 
            inc.id === selectedIncidence.id 
                ? { ...inc, status: editStatus, observations: observations.trim() } 
                : inc
        ));
        
        alert('Incidencia actualizada con éxito.');
        setSelectedIncidence(null);
    };

    const handleNotifySolicitante = () => {
        if (!selectedIncidence) return;

        // 1. Guardar los cambios de la incidencia
        setIncidences(prev => prev.map(inc => 
            inc.id === selectedIncidence.id 
                ? { ...inc, status: editStatus, observations: observations.trim() } 
                : inc
        ));

        // 2. Buscar al usuario por correo para obtener su id
        const targetUser = globalUsers.find(u => u.email.toLowerCase() === selectedIncidence.userEmail.toLowerCase());
        const notificationUserId = targetUser ? targetUser.id : selectedIncidence.userEmail;

        // 3. Crear el objeto de notificación
        if (setNotifications) {
            const newNotification: AppNotification = {
                id: Math.random().toString(36).substr(2, 9),
                userId: notificationUserId,
                title: `Incidencia ${editStatus === 'Resuelto' ? 'Resuelta' : 'Descartada'}`,
                description: `Tu reporte #${selectedIncidence.id} (${selectedIncidence.moduleName}) ha sido marcado como ${editStatus.toLowerCase()}.${observations.trim() ? ` Observaciones: ${observations.trim()}` : ''}`,
                date: new Date().toISOString(),
                isRead: false,
                type: 'system'
            };

            setNotifications(prev => [newNotification, ...(prev || [])]);
        }

        alert('Incidencia actualizada y resolución notificada al solicitante con éxito.');
        setSelectedIncidence(null);
    };

    const total = incidences.length;
    const pending = incidences.filter(i => i.status === 'Pendiente').length;
    const resolved = incidences.filter(i => i.status === 'Resuelto').length;

    const filtered = incidences.filter(inc => {
        const matchesSearch = 
            inc.actionDescription.toLowerCase().includes(searchQuery.toLowerCase()) ||
            inc.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            inc.userEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
            inc.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            inc.moduleName.toLowerCase().includes(searchQuery.toLowerCase());
            
        const matchesStatus = statusFilter === 'Todos' || inc.status === statusFilter;
        const matchesPriority = priorityFilter === 'Todos' || inc.priority === priorityFilter;

        return matchesSearch && matchesStatus && matchesPriority;
    });

    return (
        <div className="p-4 md:p-8 space-y-6">
            <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 bg-white dark:bg-[#1a2634] p-4 md:p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm animate-in fade-in duration-200">
                <div>
                    <h2 className="text-lg font-black text-slate-800 dark:text-white uppercase leading-none">Registro de Incidencias</h2>
                    <p className="text-xs text-slate-500 mt-1">Inspección de fallas y reportes recibidos desde terreno.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-[#1a2634] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Total Reportes</p>
                        <p className="text-3xl font-black text-slate-800 dark:text-white mt-1">{total}</p>
                    </div>
                    <span className="material-symbols-outlined text-indigo-500 text-[40px] opacity-25">bug_report</span>
                </div>
                <div className="bg-white dark:bg-[#1a2634] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Pendientes</p>
                        <p className="text-3xl font-black text-rose-600 dark:text-rose-400 mt-1">{pending}</p>
                    </div>
                    <span className="material-symbols-outlined text-rose-500 text-[40px] opacity-25 animate-pulse">error</span>
                </div>
                <div className="bg-white dark:bg-[#1a2634] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Resueltos</p>
                        <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{resolved}</p>
                    </div>
                    <span className="material-symbols-outlined text-emerald-500 text-[40px] opacity-25">verified</span>
                </div>
            </div>

            <div className="bg-white dark:bg-[#1a2634] p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:w-80">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-sm">search</span>
                    <input
                        type="text"
                        placeholder="Buscar por usuario, obra, sección..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs outline-none focus:ring-2 focus:ring-rose-500 dark:text-white"
                    />
                </div>
                <div className="flex gap-3 w-full md:w-auto">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as any)}
                        className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs outline-none cursor-pointer font-bold text-slate-700 dark:text-slate-200"
                    >
                        <option value="Todos">Todos los Estados</option>
                        <option value="Pendiente">Pendientes</option>
                        <option value="Resuelto">Resueltos</option>
                        <option value="Descartado">Descartados</option>
                    </select>

                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value as any)}
                        className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs outline-none cursor-pointer font-bold text-slate-700 dark:text-slate-200"
                    >
                        <option value="Todos">Todas las Prioridades</option>
                        <option value="Baja">Prioridad Baja</option>
                        <option value="Media">Prioridad Media</option>
                        <option value="Alta">Prioridad Alta</option>
                    </select>
                </div>
            </div>

            <div className="bg-white dark:bg-[#1a2634] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-[#1e2c3c] border-b border-slate-200 dark:border-slate-800">
                                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">Código / Fecha</th>
                                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">Usuario y Obra</th>
                                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">Módulo y Acción</th>
                                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest text-center">Prioridad</th>
                                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest text-center">Estado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {filtered.map(inc => (
                                <tr
                                    key={inc.id}
                                    onClick={() => setSelectedIncidence(inc)}
                                    className="hover:bg-slate-50 dark:hover:bg-[#1e2a3b] transition-colors cursor-pointer"
                                >
                                    <td className="p-4">
                                        <p className="font-bold text-slate-800 dark:text-white text-xs">{inc.id}</p>
                                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">{new Date(inc.creationDate).toLocaleDateString()}</p>
                                    </td>
                                    <td className="p-4">
                                        <p className="font-bold text-slate-800 dark:text-white text-xs">{inc.userName}</p>
                                        <p className="text-[10px] text-slate-500 block truncate max-w-[180px]">{inc.projectName}</p>
                                    </td>
                                    <td className="p-4">
                                        <p className="font-extrabold text-[10px] text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">{inc.moduleName}</p>
                                        <p className="text-xs text-slate-600 dark:text-slate-300 block truncate max-w-[240px] mt-0.5" title={inc.actionDescription}>
                                            {inc.actionDescription}
                                        </p>
                                    </td>
                                    <td className="p-4 text-center">
                                        <span className={`inline-flex px-2 py-0.5 text-[9px] font-black uppercase rounded-full ${
                                            inc.priority === 'Alta' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                            inc.priority === 'Media' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                            'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                        }`}>
                                            {inc.priority}
                                        </span>
                                    </td>
                                    <td className="p-4 text-center">
                                        <span className={`inline-flex px-2.5 py-0.5 text-[10px] font-black uppercase rounded-full ${
                                            inc.status === 'Resuelto' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                            inc.status === 'Descartado' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' :
                                            'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 animate-pulse'
                                        }`}>
                                            {inc.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="p-12 text-center text-slate-400 italic text-sm">
                                        No hay incidencias que coincidan con los filtros aplicados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedIncidence && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-3xl overflow-hidden flex flex-col h-auto max-h-[85vh] animate-in scale-up">
                        <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-5 flex justify-between items-center text-white shrink-0 border-b border-slate-200 dark:border-slate-800">
                            <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-[24px]">bug_report</span>
                                <div className="flex flex-col leading-tight">
                                    <span className="font-extrabold text-[15px] uppercase tracking-wide">Falla #{selectedIncidence.id}</span>
                                    <span className="text-[10px] text-slate-300 font-medium">Auditoría técnica de código</span>
                                </div>
                            </div>
                            <button onClick={() => setSelectedIncidence(null)} className="hover:bg-white/20 p-1.5 rounded-full transition-colors flex items-center justify-center">
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                            <div className="space-y-4 border-r border-slate-100 dark:border-slate-800 pr-0 md:pr-6">
                                <h4 className="text-[10px] font-black text-rose-500 uppercase tracking-widest">Información de Origen</h4>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Usuario Reportando</label>
                                        <p className="text-xs font-bold text-slate-800 dark:text-slate-200 mt-0.5">{selectedIncidence.userName}</p>
                                        <p className="text-[10px] text-slate-400 font-mono">{selectedIncidence.userEmail}</p>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Obra / Proyecto</label>
                                        <p className="text-xs font-bold text-slate-800 dark:text-slate-200 mt-0.5">{selectedIncidence.projectName}</p>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Módulo / Sección</label>
                                        <p className="text-xs font-extrabold text-indigo-600 dark:text-indigo-400 mt-0.5 uppercase tracking-wide">{selectedIncidence.moduleName}</p>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Fecha de Reporte</label>
                                        <p className="text-xs font-mono text-slate-700 dark:text-slate-300 mt-0.5">{new Date(selectedIncidence.creationDate).toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Acción que ejecutaba</label>
                                        <p className="text-xs text-slate-800 dark:text-slate-200 mt-1 bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 leading-normal">{selectedIncidence.actionDescription}</p>
                                    </div>
                                    {selectedIncidence.screenshot && (
                                        <div className="pt-2">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Captura Adjuntada</label>
                                            <div className="mt-1 cursor-pointer overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm relative group hover:border-indigo-500/50 transition-colors" onClick={() => setIsZoomOpen(true)}>
                                                <img src={selectedIncidence.screenshot} alt="Visual Attachment" className="w-full max-h-40 object-cover group-hover:scale-105 transition-transform animate-in fade-in" />
                                                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 flex items-center justify-center transition-colors">
                                                    <span className="material-symbols-outlined text-white text-[24px] opacity-0 group-hover:opacity-100 transition-opacity">zoom_in</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-4 flex flex-col justify-between">
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black text-rose-500 uppercase tracking-widest">Falla y Resolución</h4>
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Resultado Esperado</label>
                                        <p className="text-xs text-slate-800 dark:text-slate-200 mt-1 leading-normal bg-slate-50 dark:bg-slate-800/40 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 italic">
                                            "{selectedIncidence.expectedResult}"
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Resultado Obtenido (Error)</label>
                                        <p className="text-xs text-slate-800 dark:text-slate-200 mt-1 leading-normal bg-rose-50/50 dark:bg-rose-950/20 p-2.5 rounded-lg border border-rose-100 dark:border-rose-900/30 font-bold">
                                            "{selectedIncidence.actualResult}"
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 pt-2">
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block ml-0.5">Estado de Resolución</label>
                                            <select
                                                value={editStatus}
                                                onChange={(e) => setEditStatus(e.target.value as any)}
                                                className="w-full px-2.5 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none cursor-pointer font-bold text-slate-700 dark:text-slate-200 focus:ring-1 focus:ring-indigo-500"
                                            >
                                                <option value="Pendiente">Pendiente</option>
                                                <option value="Resuelto">Resuelto</option>
                                                <option value="Descartado">Descartado</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block ml-0.5">Prioridad Reportada</label>
                                            <span className={`w-full inline-flex justify-center items-center py-2 text-[10px] font-black uppercase rounded-lg border ${
                                                selectedIncidence.priority === 'Alta' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/10 dark:text-red-400' :
                                                selectedIncidence.priority === 'Media' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/10' :
                                                'bg-slate-50 text-slate-600 border-slate-200'
                                            }`}>
                                                {selectedIncidence.priority}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider block ml-0.5">Observaciones Técnicas (Código)</label>
                                        <textarea
                                            value={observations}
                                            onChange={(e) => setObservations(e.target.value)}
                                            placeholder="Detalla qué parches realizaste..."
                                            rows={3}
                                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-indigo-500 resize-none dark:text-white"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                                    <button
                                        onClick={() => setSelectedIncidence(null)}
                                        className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-white transition-colors"
                                    >
                                        Cerrar sin guardar
                                    </button>
                                    {(editStatus === 'Resuelto' || editStatus === 'Descartado') && (
                                        <button
                                            onClick={handleNotifySolicitante}
                                            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md hover:shadow-lg transition-all active:scale-95 flex items-center gap-1.5"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">notifications_active</span>
                                            Notificar Resolucion al Solicitante
                                        </button>
                                    )}
                                    <button
                                        onClick={handleSaveDetail}
                                        className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md hover:shadow-lg transition-all active:scale-95"
                                    >
                                        Guardar Cambios
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isZoomOpen && selectedIncidence && selectedIncidence.screenshot && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200 cursor-zoom-out" onClick={() => setIsZoomOpen(false)}>
                    <div className="relative max-w-4xl max-h-[90vh] overflow-hidden flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <img src={selectedIncidence.screenshot} alt="Visual Zoom Attachment" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl border border-slate-800 cursor-default animate-in zoom-in-95" />
                        <button onClick={() => setIsZoomOpen(false)} className="absolute top-4 right-4 bg-slate-900/60 hover:bg-slate-900 text-white rounded-full p-2 hover:scale-110 transition-transform">
                            <span className="material-symbols-outlined text-[24px]">close</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalAdminPanel;
